// ProductTemplate.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDate } from './../utils/date';

import { HorizontalLine, NotFound, Section, Title } from './../components';

import { FaArrowLeft, FaGithub } from 'react-icons/fa';
import { LuSquareArrowOutUpRight } from 'react-icons/lu';

import { projectData } from './../data';

// Simulated API helper function returning a Promise
const fetchProjectBySlug = (slug) => {
  return new Promise((resolve) => {
    const project = projectData[slug];
    resolve(project || null);
  });
};

const getLabelTitle = (label) => {
  switch (label) {
    case 'features':
      return 'Key features';
    case 'contribution':
      return 'My role';
    case 'challenges':
      return 'Challenges';
    case 'learnings':
      return 'Learnings';
    default:
      return null;
  }
};

const ItemList = (item) => {
  return (
    <>
      {Array.isArray(item.item) && item.item.length > 0 && (
        <div className="flex flex-col gap-y-3">
          <Title as="h3">{getLabelTitle(item.label)}</Title>
          <div className="flex flex-col pl-5 gap-y-2 text-midground">
            {item.item.map((value, index) => (
              <ul key={index} className="list-disc list-outside">
                <li>{value}</li>
              </ul>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

const hasOverview = (project) =>
  Boolean(
    project.overview ||
      (Array.isArray(project.features) && project.features.length > 0) ||
      (Array.isArray(project.stack) && project.stack.length > 0),
  );

const hasTechnicalDetails = (project) =>
  Boolean(
    project.details ||
      (Array.isArray(project.role_contribution) &&
        project.role_contribution.length > 0) ||
      (Array.isArray(project.challenges) && project.challenges.length > 0) ||
      (Array.isArray(project.learnings) && project.learnings.length > 0),
  );

const MultilineText = ({ text, className = '' }) => {
  if (!text) return null;

  return <div className={`${className} whitespace-pre-line`}>{text}</div>;
};

const ProjectTemplate = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjectBySlug(slug).then((data) => {
      setProject(data);
      setLoading(false);
    });
  }, [slug]);

  if (loading)
    return (
      <div className="min-h-screen p-8 text-center">Loading project...</div>
    );
  if (!project) return <NotFound />;

  const formattedDate = formatDate(project?.date);

  return (
    <div className="relative flex flex-col w-full mx-auto mt-20 gap-y-5 max-w-7xl">
      {/* Section 1: Overview */}
      <Section id="overview" className="space-y-2 layout">
        <Link
          className="flex flex-row items-center mb-8 font-normal transition duration-300 w-fit gap-x-2 text-foreground hover:text-midground"
          to="/#projects"
        >
          <FaArrowLeft />
          <span>Back to Home</span>
        </Link>

        <div className="flex items-center justify-between">
          <h2 className="text-2xl md:text-5xl text-midground">
            {project.title}
          </h2>
          <h3 className='text-lg md:text-2xl text-midground'>
            {formattedDate}
          </h3>
        </div>

        {project.subtitle && (
          <div className="flex flex-row mb-5">
            <h3 className="italic text-md md:text-xl text-midground-accent">
              {project.subtitle}
            </h3>
          </div>
        )}

        {project.description && (
          <MultilineText
            text={project.description}
            className="pb-5 leading-relaxed text-midground"
          />
        )}
        
        {/* Demo & Github link */}
        <div className="flex flex-col items-start justify-start sm:items-center sm:flex-row gap-x-3 gap-y-10">
          <div className="flex flex-row gap-x-5">
            {project.demo && (
              <a
                href={project.demo}
                target="_blank"
                rel="noreferrer"
                className="flex flex-row items-center justify-center h-12 px-4 py-2 text-sm w-fit gap-x-3 lg:text-base gradient-button-2 group"
              >
                <span className="whitespace-nowrap">Live Demo</span>
                <div className="relative w-5 h-5 overflow-hidden">
                  <div className="absolute transition-all duration-200 group-hover:-translate-y-5 group-hover:translate-x-4">
                    <LuSquareArrowOutUpRight className="w-5 h-5" />
                    <LuSquareArrowOutUpRight className="w-5 h-5 -translate-x-4" />
                  </div>
                </div>
              </a>
            )}
            {project.github && (
              <a
                href={project.github}
                target="_blank"
                rel="noreferrer"
                className="flex flex-row items-center h-12 px-3 text-sm gap-x-3 w-fit lg:text-base gradient-button-2 group"
              >
                <div className="relative overflow-hidden">
                  <div className="duration-200 group-hover:-translate-y-5">
                    <FaGithub className="w-5 h-5 translate-y-0 transition group-hover:-translate-y-[150%]" />
                    <FaGithub className="w-5 h-5 absolute translate-y-[150%] transition group-hover:translate-y-0" />
                  </div>
                </div>
              </a>
            )}
          </div>
        </div>
      </Section>

      <HorizontalLine />

      <Section id="intro" className="flex flex-col layout scroll-mt-8">
        {/* Section 1: Intro & Key Features */}
        {hasOverview(project) && (
          <>
            <Title as="h2" className="!mb-0">
              Overview
            </Title>
            {project.overview && (
              <MultilineText
                text={project.overview}
                className="mt-3 mb-5 leading-relaxed text-midground"
              />
            )}

            <div className="mt-3">
              <ItemList item={project.features} label="features" />
            </div>
            

            {Array.isArray(project.stack) && project.stack.length > 0 && (
              <div className="flex flex-col gap-y-1">
                <Title as="h3" className="mt-6 mb-3">
                  Technology Stack
                </Title>
                <div className="flex flex-row flex-wrap gap-3">
                  {project.stack.map((technology, index) => (
                    <div
                      key={index}
                      className="px-3 py-2 text-sm font-semibold text-white border border-black rounded-full gradient-button-2"
                    >
                      {technology}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Section 2: Technical Details & Code Contribution */}
        {hasTechnicalDetails(project) && (
          <div className="flex flex-col mt-10">
            {/* Details on how I made it
            <Title as="h3">Technical Details</Title>
            {project.details && (
              <div className="mt-3 mb-10 text-midground">{project.details}</div>
            )}
            */}
            
            {/* My Contribution (with team vs individual work) */}
            <ItemList item={project.role_contribution} label="contribution" />

            {project.challenges.length > 0 && (
              <div className="flex flex-col justify-between w-full my-10 leading-relaxed gap-x-16 gap-y-10 md:flex-row">
                <div className="w-full md:w-1/2">
                  <ItemList item={project.challenges} label="challenges" />
                </div>
                {project.challenges.length > 0 && (
                  <div className="w-full md:w-1/2">
                    <ItemList item={project.learnings} label="learnings" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {/* Section 3: Image */}
        {project.image && (
          <img
            src={project.image}
            alt={project.title}
            className="aspect-[3/2] w-full h-auto object-cover rounded-3xl border-2 border-black mt-10"
          />
        )}
      </Section>

      {/* Section 4: Also see blog post in external link */}
    </div>
  );
};

export default ProjectTemplate;
